import ReportChartPage, {
  ReportChartConfig,
} from '@app/components/reports/components/report-chart-page';
import {
  carbonIntensityAxisProps,
  carbonIntensityChartConfig,
  emissionsChartConfig,
  emissionsWithCarbonIntensityConfig,
  emissionsWithGoodsTypeConfig,
  emissionsWithSpendConfig,
  spendAxisProps,
  spendChartConfig,
  totalEmissionsAxisProps,
} from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useEmissionsChartVariants,
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import { useAccount } from '@shared/api';
import DualAxisBarChart from '@shared/components/chart/dual-axis-bar-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import { getCurrencySymbol } from '@shared/lib/format';
import { truncateText } from '@shared/lib/utils';

interface EmissionsByTagProps extends ReportSectionComponent {}

export default function EmissionsByTag({ reportType }: EmissionsByTagProps) {
  const {
    result: { emissionsByTag },
    downloadMode,
  } = useGetReportSectionData(reportType);
  const { variant, setVariant, availableVariants, variants } =
    useEmissionsChartVariants(reportType, 'tag');
  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const { data: account } = useAccount(true);
  const currencySymbol = getCurrencySymbol(account?.defaultCurrency);

  const emissionsWithCarbonIntensityData = emissionsByTag;
  const emissionsWithGoodsType = emissionsByTag.map((item) => {
    const result: Record<string, number | string> = { name: item.name };
    item.byGoodsType.forEach((type) => {
      result[type.name.toLocaleLowerCase()] = type.emissionsTonnes;
    });
    return result;
  });

  const emissionsWithSpendData = emissionsByTag.map((item) => ({
    ...item,
    totalSpend: item.totalSpend != null ? Number(item.totalSpend) : 0,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map((d) => d.emissionsTonnes)
  );
  const carbonIntensityDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map((d) => d.carbonIntensity)
  );
  const spendDataMax = Math.max(
    0,
    ...emissionsWithSpendData.map((d) => d.totalSpend)
  );

  const defaultChartProps = {
    nameKey: 'name',
    dataKey: 'name',
    margin: { top: 40, right: 10, left: 0, bottom: 0 },
    className: 'h-max max-h-[calc(var(--content-title-height)-20rem)] w-full',
    XAxisProps: {
      tickFormatter: (value: string) => truncateText(value, 12),
    },
  };

  let config: ReportChartConfig = emissionsChartConfig;
  if (variant === variants.goodsType) {
    config = emissionsWithGoodsTypeConfig;
  } else if (variant === variants.intensity) {
    config = emissionsWithCarbonIntensityConfig;
  } else if (variant === variants.spend) {
    config = emissionsWithSpendConfig(currencySymbol);
  }

  return (
    <ReportChartPage
      title="Total emissions per tag"
      description={`This chart shows the total emissions from each tag used to tag your ${reportTypeCopy.singular} imports, ordered by highest emissions. Emissions from untagged ${reportTypeCopy.plural} imports are not included.`}
      variantOptions={availableVariants}
      variant={variant}
      setVariant={setVariant}
      variantsEnabled={!downloadMode && availableVariants.length > 1}
      chartConfig={config}
    >
      {variant === variants.emissions && (
        <SimpleBarChart
          {...defaultChartProps}
          chartConfig={emissionsChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
        />
      )}
      {variant === variants.intensity && (
        <DualAxisBarChart
          {...defaultChartProps}
          leftAxisConfig={emissionsChartConfig}
          rightAxisConfig={carbonIntensityChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={carbonIntensityAxisProps(carbonIntensityDataMax)}
        />
      )}
      {variant === variants.goodsType && (
        <SimpleBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsWithGoodsType}
          chartConfig={emissionsWithGoodsTypeConfig}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
        />
      )}
      {variant === variants.spend && (
        <DualAxisBarChart
          {...defaultChartProps}
          leftAxisConfig={emissionsChartConfig}
          rightAxisConfig={spendChartConfig(currencySymbol)}
          chartData={emissionsWithSpendData}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={spendAxisProps(spendDataMax, currencySymbol)}
        />
      )}
    </ReportChartPage>
  );
}
